import { useWorkspaceText } from '../interface-copy';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { WorkspaceIcon } from '../icons';
import { pickerKeyAction } from '../picker-keyboard';
import { matchTitle, type TreeIndex } from './tree';
import type { TreePage } from './types';

type Option =
  | { kind: 'page'; page: TreePage; depth: number; hint: string | null }
  | { kind: 'create'; title: string };

/**
 * Choose a page by title: the tree while the search is empty, matching pages
 * (with where they sit) once something is typed. Arrow keys move, Enter
 * chooses, Escape closes. With `onCreate`, a typed title can also become a
 * new page.
 */
export function PagePicker({
  index,
  label,
  placeholder = 'Search pages',
  exclude,
  createUnder,
  onChoose,
  onCreate,
  onClose,
  busy = false,
}: {
  index: TreeIndex;
  label: string;
  placeholder?: string;
  exclude?: Set<string>;
  /** Where a new page would go, for the create option's wording. */
  createUnder?: string;
  onChoose: (page: TreePage) => void;
  onCreate?: (title: string) => void;
  onClose: () => void;
  busy?: boolean;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const id = useId();
  useEffect(() => input.current?.focus(), []);
  const options = useMemo<Option[]>(() => {
    const rows = index.flat().filter(({ page }) => !exclude?.has(page.id));
    const needle = query.trim();
    const pages: Option[] = needle
      ? rows
          .filter(({ page }) => matchTitle(page.title, needle))
          .map(({ page }) => ({
            kind: 'page',
            page,
            depth: 0,
            hint: index.ancestors(page.id).at(-1)?.title ?? null,
          }))
      : rows.map(({ page, depth }) => ({ kind: 'page', page, depth, hint: null }));
    return onCreate && needle ? [...pages, { kind: 'create', title: needle }] : pages;
  }, [index, exclude, query, Boolean(onCreate)]);
  useEffect(() => setSelected(0), [query]);
  useEffect(() => {
    list.current
      ?.querySelector<HTMLElement>(`[data-index="${selected}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [selected]);
  const choose = (option: Option | undefined) => {
    if (!option || busy) return;
    if (option.kind === 'page') onChoose(option.page);
    else onCreate?.(option.title);
  };
  const optionId = (position: number) => `${id}-option-${position}`;
  return (
    <div className="ws-page-picker">
      <label className="ws-page-picker-search">
        <WorkspaceIcon name="search" size={14} />
        <span className="ws-sr-only">{label}</span>
        <input
          ref={input}
          type="search"
          role="combobox"
          aria-expanded="true"
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          aria-activedescendant={options.length ? optionId(selected) : undefined}
          value={query}
          placeholder={placeholder}
          disabled={busy}
          onChange={(event) => setQuery(event.target.value)}
          onInput={(event) => setQuery((event.target as HTMLInputElement).value)}
          onKeyDown={(event) => {
            if (event.nativeEvent.isComposing) return;
            const action = pickerKeyAction(event.key, selected, options.length);
            if (!action) return;
            event.preventDefault();
            event.stopPropagation();
            if (action.kind === 'close') onClose();
            else if (action.kind === 'move') setSelected(action.index);
            else choose(options[action.index]);
          }}
        />
      </label>
      <ul
        className="ws-page-picker-list"
        role="listbox"
        id={`${id}-list`}
        aria-label={label}
        ref={list}
      >
        {options.map((option, position) => (
          <li
            key={option.kind === 'page' ? option.page.id : 'create'}
            id={optionId(position)}
            role="option"
            aria-selected={position === selected}
            data-index={position}
            data-kind={option.kind}
            className="ws-page-picker-option"
            style={
              option.kind === 'page'
                ? { paddingInlineStart: `${10 + option.depth * 14}px` }
                : undefined
            }
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => setSelected(position)}
            onClick={() => choose(option)}
          >
            {option.kind === 'page' ? (
              <>
                <WorkspaceIcon name={option.page.isRoot ? 'home' : 'knowledge'} size={14} />
                <span>
                  <strong>{option.page.title}</strong>
                  {option.hint && <small>{option.hint}</small>}
                </span>
              </>
            ) : (
              <>
                <WorkspaceIcon name="plus" size={14} />
                <span>
                  {uiRich('New page “{value1}”{value2}', {
                    value1: option.title,
                    value2: createUnder ? ` under ${createUnder}` : '',
                  })}
                </span>
              </>
            )}
          </li>
        ))}
        {!options.length && (
          <li className="ws-page-picker-empty">
            {uiRich('No pages match “{value1}”.', { value1: query.trim() })}
          </li>
        )}
      </ul>
    </div>
  );
}
